/**
 * TicketFactura - Componente de recibo térmico 80mm para facturas de paciente.
 * Sigue el mismo patrón que TicketTurno + useReactToPrint.
 */

import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { Factura } from "@/hooks/use-facturacion";

// =============================================================================
// Componente
// =============================================================================

interface TicketFacturaProps {
  factura: Factura;
}

export const TicketFactura = forwardRef<HTMLDivElement, TicketFacturaProps>(({ factura }, ref) => {
  const { t } = useTranslation();

  const totalPagado = parseFloat(factura.total_pagado || "0");
  const total = parseFloat(factura.total || "0");
  const vuelto = Math.max(0, totalPagado - total);

  const formatMonto = (val: string | number) => `L. ${parseFloat(String(val)).toFixed(2)}`;

  return (
    <div
      ref={ref}
      className="w-[280px] bg-white p-4 font-mono text-black text-[11px] print:shadow-none"
      style={{ fontFamily: "monospace", color: "#000000" }}
    >
      {/* Header */}
      <div className="text-center border-b-2 border-dashed border-black pb-3 mb-3">
        <img
          src="/logo-lab.png"
          alt="Laboratorio Molina Valdez"
          className="mx-auto mb-2"
          style={{ maxWidth: "140px", maxHeight: "70px", objectFit: "contain" }}
        />
        <p className="font-bold text-base">Laboratorio Molina Valdez</p>
        <p className="text-xs">RTN: 0801-1990-00001</p>
        <p className="text-xs">{factura.sucursal_nombre}</p>
        {factura.sucursal_direccion && <p className="text-xs">{factura.sucursal_direccion}</p>}
        <p className="text-xs mt-1">
          {format(new Date(`${factura.fecha}T00:00:00`), "EEEE, d 'de' MMMM 'de' yyyy", {
            locale: es,
          })}
        </p>
      </div>

      {/* Número de factura */}
      <div className="pt-2">
        <p className="text-xs font-bold tracking-wide">FACTURA {factura.numero}</p>
      </div>

      {/* Datos del cliente / paciente */}
      <div className="border-t-2 border-dashed border-black pt-3 mt-3 space-y-1">
        {/* Modo "EMPRESA" o "AMBOS": mostrar nombre_cliente y RTN */}
        {(factura.modo_facturacion === "EMPRESA" || factura.modo_facturacion === "AMBOS") && (
          <div data-testid="ticket-seccion-empresa">
            {factura.nombre_cliente && (
              <div className="flex justify-between gap-2">
                <span className="font-bold shrink-0">
                  {t("facturacion.detail.cliente", "Cliente")}:
                </span>
                <span
                  className="font-bold text-right break-words"
                  data-testid="ticket-nombre-cliente"
                >
                  {factura.nombre_cliente}
                </span>
              </div>
            )}
            {factura.rtn_cliente && (
              <div className="flex justify-between gap-2">
                <span className="font-bold shrink-0">RTN:</span>
                <span className="font-bold text-right" data-testid="ticket-rtn-cliente">
                  {factura.rtn_cliente}
                </span>
              </div>
            )}
          </div>
        )}
        {/* Modo "PACIENTE" o "AMBOS": mostrar ID y nombre del paciente */}
        {(factura.modo_facturacion === "PACIENTE" || factura.modo_facturacion === "AMBOS") && (
          <div data-testid="ticket-seccion-paciente">
            {factura.paciente_identificacion && (
              <div className="flex justify-between gap-2">
                <span className="font-bold shrink-0">ID:</span>
                <span className="font-bold text-right" data-testid="ticket-paciente-id">
                  {factura.paciente_identificacion}
                </span>
              </div>
            )}
            {factura.paciente_nombre && (
              <div className="flex justify-between gap-2">
                <span className="font-bold shrink-0">
                  {t("facturacion.detail.paciente", "Paciente")}:
                </span>
                <span
                  className="font-bold text-right break-words"
                  data-testid="ticket-paciente-nombre"
                >
                  {factura.paciente_nombre}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Datos fiscales SAR */}
      <div className="border-t-2 border-dashed border-black pt-3 mt-3 space-y-1 text-xs">
        <div>
          <span className="font-bold">CAI: </span>
          <span className="break-all">{factura.cai_codigo}</span>
        </div>
        {factura.cai_rango_inicio && (
          <div>
            <span className="font-bold">Rango: </span>
            <span>
              {factura.cai_rango_inicio} - {factura.cai_rango_fin}
            </span>
          </div>
        )}
        {factura.cai_fecha_limite && (
          <div>
            <span className="font-bold">Fecha límite: </span>
            <span>{format(new Date(factura.cai_fecha_limite), "dd/MM/yyyy")}</span>
          </div>
        )}
      </div>

      {/* Orden (una sola vez si todas son la misma) */}
      {(() => {
        const ordenNums = [...new Set(factura.detalles.map((d) => d.orden_numero).filter(Boolean))];
        if (ordenNums.length === 1) {
          return (
            <div className="border-t-2 border-dashed border-black pt-2 mt-1">
              <p className="text-xs">
                Orden: <span className="font-bold">{ordenNums[0]}</span>
              </p>
            </div>
          );
        }
        return null;
      })()}

      {/* Ítems */}
      <div className="border-t-2 border-dashed border-black pt-3 mt-3">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black">
              <th className="text-center pb-1 w-[18px]">Cant</th>
              <th className="text-left pb-1 pl-1">Descripción</th>
              <th className="text-right pb-1">P.Unit</th>
              <th className="text-right pb-1 pl-1">Importe</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const ordenNums = [
                ...new Set(factura.detalles.map((d) => d.orden_numero).filter(Boolean)),
              ];
              const multiOrden = ordenNums.length > 1;

              const renderFila = (detalle: (typeof factura.detalles)[0]) => (
                <tr key={detalle.id} className="border-b border-dashed border-black">
                  <td className="text-center py-1">{detalle.cantidad}</td>
                  <td className="py-1 pl-1 pr-1">{detalle.descripcion}</td>
                  <td className="text-right py-1 whitespace-nowrap">
                    {formatMonto(detalle.precio_unitario)}
                  </td>
                  <td className="text-right py-1 pl-1 whitespace-nowrap">
                    {formatMonto(detalle.subtotal)}
                  </td>
                </tr>
              );

              if (!multiOrden) {
                return factura.detalles.map(renderFila);
              }

              // Multi-orden: agrupar por orden
              const grupos = new Map<string, typeof factura.detalles>();
              for (const d of factura.detalles) {
                const key = d.orden_numero || "";
                if (!grupos.has(key)) grupos.set(key, []);
                grupos.get(key)!.push(d);
              }

              return [...grupos.entries()].map(([ordenNum, detalles]) => (
                <>
                  {ordenNum && (
                    <tr key={`header-${ordenNum}`}>
                      <td colSpan={4} className="text-[9px] font-bold pt-1 pb-0">
                        Orden: {ordenNum}
                      </td>
                    </tr>
                  )}
                  {detalles.map(renderFila)}
                </>
              ));
            })()}
          </tbody>
        </table>
      </div>

      {/* Totales */}
      <div className="border-t-2 border-dashed border-black pt-3 mt-3 space-y-1 text-xs">
        <div className="flex justify-between">
          <span>{t("facturacion.detail.subtotal", "Subtotal")}:</span>
          <span>{formatMonto(factura.subtotal)}</span>
        </div>

        {/* Descuentos por orden */}
        {factura.ordenes?.map((orden) => {
          const descPct = parseFloat(orden.descuento_porcentaje || "0");
          const descLps = parseFloat(orden.descuento_lempiras || "0");
          const listaPct = parseFloat(orden.lista_precio_porcentaje || "0");
          const hasDesc = descPct > 0 || descLps > 0 || listaPct > 0;
          if (!hasDesc) return null;
          // total de la orden ya tiene los descuentos aplicados
          const totalOrden = parseFloat(orden.total || "0");
          return (
            <div key={orden.id} className="pl-1 border-l-2 border-dashed border-gray-400 ml-1">
              <p className="text-[9px] text-gray-500">Orden {orden.numero}:</p>
              {listaPct > 0 && orden.lista_precio_nombre && (
                <div className="flex justify-between">
                  <span className="text-[10px]">
                    Desc. {orden.lista_precio_nombre} ({listaPct.toFixed(0)}%):
                  </span>
                  <span className="text-[10px]">aplicado</span>
                </div>
              )}
              {descPct > 0 && (
                <div className="flex justify-between">
                  <span className="text-[10px]">Desc. adicional ({descPct.toFixed(0)}%):</span>
                  <span className="text-[10px]">aplicado</span>
                </div>
              )}
              {descLps > 0 && (
                <div className="flex justify-between">
                  <span className="text-[10px]">Desc. fijo:</span>
                  <span className="text-[10px]">- {formatMonto(orden.descuento_lempiras)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold">
                <span className="text-[10px]">Total orden:</span>
                <span className="text-[10px]">{formatMonto(totalOrden)}</span>
              </div>
            </div>
          );
        })}

        {/* Descuento global de factura */}
        {parseFloat(factura.descuento) > 0 && (
          <div className="flex justify-between">
            <span>{t("facturacion.detail.descuento", "Descuento")}:</span>
            <span>- {formatMonto(factura.descuento)}</span>
          </div>
        )}
        {parseFloat(factura.descuento_pagos_directos || "0") > 0 && (
          <div className="flex justify-between">
            <span>Pagado directamente:</span>
            <span>- {formatMonto(factura.descuento_pagos_directos)}</span>
          </div>
        )}

        {parseFloat(factura.exento) > 0 && (
          <div className="flex justify-between">
            <span>{t("facturacion.detail.exento", "Exento")}:</span>
            <span>{formatMonto(factura.exento)}</span>
          </div>
        )}
        {parseFloat(factura.impuesto) > 0 && (
          <div className="flex justify-between">
            <span>{t("facturacion.detail.impuesto", "ISV (15%)")}:</span>
            <span>{formatMonto(factura.impuesto)}</span>
          </div>
        )}
        {/* Si hay pagos directos, mostrar "A pagar en factura" y "Total general" por separado */}
        {parseFloat(factura.descuento_pagos_directos || "0") > 0 ? (
          <>
            <div className="flex justify-between font-bold text-sm border-t border-black pt-1 mt-1">
              <span>A PAGAR EN FACTURA:</span>
              <span>{formatMonto(factura.total)}</span>
            </div>
            <div className="flex justify-between font-bold text-sm border-t border-black pt-1 mt-1">
              <span>TOTAL GENERAL:</span>
              <span>
                {formatMonto(
                  parseFloat(factura.total) + parseFloat(factura.descuento_pagos_directos || "0")
                )}
              </span>
            </div>
          </>
        ) : (
          <div className="flex justify-between font-bold text-sm border-t border-black pt-1 mt-1">
            <span>TOTAL:</span>
            <span>{formatMonto(factura.total)}</span>
          </div>
        )}
        {factura.monto_en_letras && <p className="text-[10px] mt-1">{factura.monto_en_letras}</p>}
      </div>

      {/* Pagos */}
      {factura.pagos.length > 0 && (
        <div className="border-t-2 border-dashed border-black pt-3 mt-3 space-y-1 text-xs">
          <p className="font-bold uppercase text-[10px]">Pagos</p>
          {factura.pagos.map((pago) => (
            <div key={pago.id} className="flex justify-between">
              <span>{pago.metodo_nombre}:</span>
              <span>{formatMonto(pago.monto)}</span>
            </div>
          ))}
          <div className="flex justify-between font-bold border-t border-dashed border-black pt-1">
            <span>Total pagado:</span>
            <span>{formatMonto(factura.total_pagado)}</span>
          </div>
        </div>
      )}

      {/* Vuelto */}
      {vuelto > 0 && (
        <div className="border-t-2 border-dashed border-black pt-3 mt-3">
          <div className="flex justify-between font-bold text-sm">
            <span>{t("facturacion.pago.vuelto", "Vuelto")}:</span>
            <span>{formatMonto(vuelto)}</span>
          </div>
        </div>
      )}

      {/* Firma */}
      <div className="mt-6 pt-3 border-t-2 border-dashed border-black">
        <div className="mt-6 mx-4">
          <div className="border-t border-black" />
          <p className="text-center text-xs mt-1">Firma</p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-4 pt-3 border-t-2 border-dashed border-black">
        <p className="text-xs">¡Gracias por su preferencia!</p>
        <p className="text-xs mt-1">{format(new Date(), "HH:mm:ss")}</p>
      </div>
    </div>
  );
});

TicketFactura.displayName = "TicketFactura";
